import { Component } from "vue-property-decorator";
import { mixins } from "vue-class-component";
import {
  BaseStore,
  LayoutStore,
  ShapeStore,
  CanopyStore,
  FrameStore,
} from "@/mixins/store";
import { Collection, ProductLineName } from "@/models/products/collection";
import { UmbrellaModel } from "@/models/products/umbrella";
import { BaseModel } from "@/models/products/base";
import { ProductType, SingleProductRes } from "@/models/products/product";
import ProductService from "@/services/product_service";
import FabricService from "@/services/fabric_service";
import { Preset, Stem } from "@/models/configurator/configurator_options";
import { APIError, NotFoundError } from "@/services/error_service";
import { ConfigFile, StartAppFile } from "@/models/configurator/config_file";
import {
  Finish,
  FinishDisplayName,
  FinishHandle,
} from "@/models/products/finish";
import * as option from "@/models/configurator/configurator_options";
import { FabricSwatch } from "@/models/products/fabric";
import { fabricTextureKey } from "@/helpers/fabric";
import { StockMixin } from "./stock";
import { FabricRules } from "./business_logic/global/fabric_rules";
@Component
export class ConfigCreator extends mixins(
  BaseStore,
  LayoutStore,
  ShapeStore,
  CanopyStore,
  FrameStore,
  StockMixin,
  FabricRules
) {
  protected productService = new ProductService();
  protected fabricService = new FabricService();

  protected async transformDataForViewerAndStartApp(): Promise<void> {
    const data = await this.transformDataToViewerJson();
    await this.$viewer.StartApp(data);
  }

  protected async transformDataToViewerJson(): Promise<StartAppFile> {
    const fabricArray = [];
    if (this.mainFabric) {
      for (const swatch of this.mainFabric) {
        fabricArray.push(swatch.fabric_scale, fabricTextureKey(swatch));
      }
    }
    const fabricArrayVentTop = [];
    if (this.ventTopFabric) {
      if (this.ventTopFabric.length > 0) {
        for (const swatch of this.ventTopFabric) {
          fabricArrayVentTop.push(swatch.fabric_scale, fabricTextureKey(swatch));
        }
      }
    }
    const fabricArrayVentMid = [];
    if (this.ventMiddleFabric) {
      if (this.ventMiddleFabric.length > 0) {
        for (const swatch of this.ventMiddleFabric) {
          fabricArrayVentMid.push(swatch.fabric_scale, fabricTextureKey(swatch));
        }
      }
    }
    const fabricArrayValance = [];
    if (this.valanceFabric) {
      if (this.valanceFabric.length > 0) {
        for (const swatch of this.valanceFabric) {
          fabricArrayValance.push(swatch.fabric_scale, fabricTextureKey(swatch));
        }
      }
    }
    const fabricArrayFringe = [];
    if (this.fringeFabric) {
      if (this.fringeFabric.length > 0) {
        for (const swatch of this.fringeFabric) {
          fabricArrayFringe.push(swatch.fabric_scale, fabricTextureKey(swatch));
        }
      }
    }
    /**
     * Exception logic for bases that include 18ST2 stems in the base's model name.
     * These are specific to giant umbrellas, which MUST have an 18ST2 stem
     */
    let base = this.baseModel ? this.baseModel.model : null;
    let baseStem = this.baseStem;
    if (base && base !== Stem.Stem18ST2 && base.indexOf("18ST2") > -1) {
      base = base.slice(0, -6);
      baseStem = Stem.Stem18ST2;
    }
    const data = {
      layout: {
        preset: this.preset,
        collection: { handle: this.collection.handle },
      },
      shape: { model: this.umbrellaModel!.model },
      base: {
        base: base,
        baseFinish: this.baseFinish ? this.baseFinish.handle : null,
        baseStem: this.baseStem,
        baseOptions: this.baseOptions ? this.baseOptions : null
      },
      frame: {
        finish: this.finish.handle,
        finial: this.finial,
        finialFinish: this.finialFinish.handle,
        bottomPole: this.bottomPole,
      },
      canopy: {
        mainCanopy: this.mainCanopy,
        mainFabric: fabricArray,
        undersideFabric: this.undersideFabric
          ? [this.undersideFabric.fabric_scale, fabricTextureKey(this.undersideFabric)]
          : [],
        ventTopFabric: fabricArrayVentTop,
        ventMiddle: this.ventMiddle,
        ventMiddleFabric: fabricArrayVentMid,
        ventTrimFabric: this.ventTrimFabric
          ? fabricTextureKey(this.ventTrimFabric)
          : null,
        mainTrimFabric: this.mainTrimFabric
          ? fabricTextureKey(this.mainTrimFabric)
          : null,
        mainTrimFabricInner: this.mainTrimFabricInner
          ? fabricTextureKey(this.mainTrimFabricInner)
          : null,
        valance: this.valance,
        valanceFabric: fabricArrayValance,
        bindingFabric: this.bindingFabric ? fabricTextureKey(this.bindingFabric) : null,
        rib: this.rib,
        ribFabric:
          !this.rib || this.rib === option.Rib.Grommets ? null : fabricArray[1], // 0 would be fabric scale
        fringe: this.fringe,
        fringeFabric: fabricArrayFringe,
      },
    } as StartAppFile;
    return data;
  }

  protected async transformDataToConfigFile(): Promise<ConfigFile> {
    /**
     * Encode a fabric reference for the SKU pipeline.
     * Returns "<mfr_code>::<group>" when the fabric carries a name.group
     * (used by the API to disambiguate duplicate mfr_codes — e.g. SENS D-020
     * across Coquette / Finesse / Enea — and harmless when emitted on a
     * non-duplicate fabric since the API only consults the suffix when the
     * bare lookup is ambiguous). Falls back to bare "<mfr_code>" otherwise.
     */
    const encodeFabric = (f: FabricSwatch): string => {
      const group = f.name && f.name.group;
      return group ? `${f.mfr_code}::${group}` : f.mfr_code;
    };

    const fabricArray = this.mainFabric.map(encodeFabric);
    const fabricArrayVentTop = this.ventTopFabric.map(encodeFabric);
    const fabricArrayVentMid = this.ventMiddleFabric.map(encodeFabric);
    const fabricArrayValance = this.valanceFabric.map(encodeFabric);
    const fabricArrayFringe = this.fringeFabric.map(encodeFabric);

    const data = {
      layout: {
        preset: this.preset,
        collection: { handle: this.collection.handle },
      },
      shape: { model: this.umbrellaModel!.model },
      base: {
        base: this.baseModel ? this.baseModel.model : null,
        baseFinish: this.baseFinish ? this.baseFinish.handle : null,
        baseStem: this.baseStem,
        baseOptions: this.baseOptions,
      },
      frame: {
        finish: this.finish.handle,
        finial: this.finial,
        finialFinish: this.finialFinish.handle,
        bottomPole: this.bottomPole,
        spigot: this.spigot,
      },
      canopy: {
        mainCanopy: this.mainCanopy,
        undersideFabric: this.undersideFabric
          ? encodeFabric(this.undersideFabric)
          : null,
        mainTrimFabric: this.mainTrimFabric
          ? encodeFabric(this.mainTrimFabric)
          : null,
        mainTrimFabricInner: this.mainTrimFabricInner
          ? encodeFabric(this.mainTrimFabricInner)
          : null,
        mainFabric: fabricArray,
        ventTopFabric: fabricArrayVentTop,
        ventMiddle: this.ventMiddle,
        ventMiddleFabric: fabricArrayVentMid,
        valanceFabric: fabricArrayValance,
        ventTrimFabric: this.ventTrimFabric
          ? encodeFabric(this.ventTrimFabric)
          : null,
        valance: this.valance,
        bindingFabric: this.bindingFabric ? encodeFabric(this.bindingFabric) : null,
        rib: this.rib,
        ribFabric:
          !this.rib || this.rib === option.Rib.Grommets ? null : fabricArray[0],
        fringe: this.fringe,
        fringeFabric: fabricArrayFringe
      },
    };
    return data;
  }

  protected setStockDefault(stock: boolean | undefined = undefined) {
    if (stock) {
      this.addStock(stock);
    } else {
      this.addStock(false);
    }
  }

  protected async transformConfigFileAndSaveToStore(
    file: ConfigFile
  ): Promise<void> {
    try {
      const data: any = JSON.parse(JSON.stringify(file));
      const fabricArray = [];
      for (const fabricCode of file.canopy.mainFabric.filter(
        (e: any) => typeof e === "string"
      )) {
        const swatch = await this.getFabricSwatch(fabricCode);
        if (!swatch || this.isEmptyObject(swatch)) {
          throw new Error();
        }
        fabricArray.push(swatch);
      }
      data.canopy.mainFabric = fabricArray;
      if (data.canopy.undersideFabric) {
        data.canopy.undersideFabric = await this.getFabricSwatch(
          file.canopy.undersideFabric!
        );
      } else {
        data.canopy.undersideFabric = null;
      }
      const fabricArrayVentTop = [];
      for (const fabricCode of file.canopy.ventTopFabric.filter(
        (e: any) => typeof e === "string"
      )) {
        const swatch = await this.getFabricSwatch(fabricCode);
        if (!swatch || this.isEmptyObject(swatch)) {
          throw new Error();
        }
        fabricArrayVentTop.push(swatch);
      }
      data.canopy.ventTopFabric = fabricArrayVentTop;
      if (file.canopy.ventMiddleFabric.length > 0) {
        const fabricArrayVentMid = [];
        for (const fabricCode of file.canopy.ventMiddleFabric.filter(
          (e: any) => typeof e === "string"
        )) {
          const swatch = await this.getFabricSwatch(fabricCode);
          if (!swatch || this.isEmptyObject(swatch)) {
            throw new Error();
          }
          fabricArrayVentMid.push(swatch);
        }
        data.canopy.ventMiddleFabric = fabricArrayVentMid;
      }
      if (file.canopy.valanceFabric.length > 0) {
        const fabricArrayValance = [];
        for (const fabricCode of file.canopy.valanceFabric.filter(
          (e: any) => typeof e === "string"
        )) {
          const swatch = await this.getFabricSwatch(fabricCode);
          if (!swatch || this.isEmptyObject(swatch)) {
            throw new Error();
          }
          fabricArrayValance.push(swatch);
        }
        data.canopy.valanceFabric = fabricArrayValance;
      }
      if (file.canopy.fringeFabric.length > 0) {
        const fabricArrayFringe = [];
        for (const fabricCode of file.canopy.fringeFabric.filter(
          (e: any) => typeof e === "string"
        )) {
          const swatch = await this.getFabricSwatch(fabricCode);
          if (!swatch || this.isEmptyObject(swatch)) {
            throw new Error();
          }
          fabricArrayFringe.push(swatch);
        }
        data.canopy.fringeFabric = fabricArrayFringe;
      }
      if (data.canopy.bindingFabric) {
        data.canopy.bindingFabric = await this.getFabricSwatch(
          file.canopy.bindingFabric!
        );
        this.hasValanceBindingOverride = !!data.canopy.fringeFabric[0] &&
          data.canopy.bindingFabric.mfr_code ===
          data.canopy.fringeFabric[0].mfr_code;
      }
      if (data.canopy.ribFabric) {
        data.canopy.ribFabric = fabricArray[0];
      }
      if (data.canopy.mainTrimFabric) {
        data.canopy.mainTrimFabric = await this.getFabricSwatch(
          file.canopy.mainTrimFabric!
        );
      }
      if (data.canopy.mainTrimFabricInner) {
        data.canopy.mainTrimFabricInner = await this.getFabricSwatch(
          file.canopy.mainTrimFabricInner!
        );
      }
      if (data.canopy.ventTrimFabric) {
        data.canopy.ventTrimFabric = await this.getFabricSwatch(
          file.canopy.ventTrimFabric!
        );
      }
      const umbrellaCollection: Collection = await this.getUmbrellaProductLines(
        data.layout.productline.handle
      );
      const addCollectionStorePromise = await this.addCollection(umbrellaCollection);
      const addPresetStorePromise = await this.addPreset(data.layout.preset ? data.layout.preset : Preset.S1);
      const model: UmbrellaModel = await this.getUmbrellaModel(data.shape.model);
      if (!model || this.isEmptyObject(model)) {
        throw new Error();
      }
      const addUmbrellaModelStorePromise = await this.addUmbrellaModel(model);
      let base: BaseModel | null = null;
      if (data.base.base) {
        //base can be null
        base = await this.getBaseModel(data.base.base);
      }
      const addAllBaseDataStorePromise = await this.addAllBaseData({
        base: base,
        baseFinish: base ? this.getFinish(data.base.baseFinish) : null,
        baseStem: base ? data.base.baseStem : null,
        baseOptions: base ? data.base.baseOptions : null
      });
      const canopy: any = { ...data.canopy };
      if (canopy.mainFabric.length > 1) {
        canopy.mainCanopy = option.Panel.Alternating;
      } else {
        canopy.mainCanopy = option.Panel.Solid;
      }
      if (canopy.ventMiddleFabric.length === 0) {
        canopy.ventMiddle = null;
      } else if (canopy.ventMiddleFabric.length > 1) {
        canopy.ventMiddle = option.Panel.Alternating;
      } else {
        canopy.ventMiddle = option.Panel.Solid;
      }
      const addAllCanopyDataStorePromise = await this.addAllCanopyData(canopy);
      const frame: any = { ...data.frame };
      frame.finialFinish = this.getFinish(data.frame.finialFinish);
      frame.finish = this.getFinish(data.frame.finish);
      // Cantilever overrides: vertex finial + no bottom pole. Eclipse always
      // SS finish; Aurora mirrors frame finish. Keeps spec/config payloads
      // and store consistent across all load paths (initial select, startOver,
      // shared view, edit mode).
      const handle = umbrellaCollection?.handle;
      if (handle === ProductLineName.Eclipse) {
        frame.finial = option.Finial.Vertex;
        frame.finialFinish = {
          handle: FinishHandle.SS,
          display_name: FinishDisplayName.SS
        };
        frame.bottomPole = null;
      } else if (handle === ProductLineName.Aurora) {
        frame.finial = option.Finial.Vertex;
        frame.finialFinish = frame.finish;
        frame.bottomPole = null;
      }
      const addAllFrameDataStorePromise = await this.addAllFrameData(frame);

      await Promise.all([
        addCollectionStorePromise,
        addPresetStorePromise,
        addUmbrellaModelStorePromise,
        addAllBaseDataStorePromise,
        addAllCanopyDataStorePromise,
        addAllFrameDataStorePromise,
      ]);
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  /**
   * Look up a single fabric swatch from a config-file fabric reference.
   * The reference is either:
   *   - "<mfr_code>"           — bare mfr_code (legacy / non-duplicate)
   *   - "<mfr_code>::<group>"  — disambiguated form for fabrics whose mfr_code
   *     is shared with another fabric document (e.g. SENS D-020 in Coquette
   *     vs. Finesse vs. Enea); "<group>" is the fabric's name.group value.
   * The "::" suffix is stripped before the API call; when present, it's forwarded
   * as the `group` filter so the API returns the correct fabric.
   */
  protected async getFabricSwatch(fabricRef: string): Promise<FabricSwatch> {
    let swatch = {} as FabricSwatch;
    const sepIdx = fabricRef.indexOf("::");
    const mfrCode = sepIdx === -1 ? fabricRef : fabricRef.substring(0, sepIdx);
    const group = sepIdx === -1 ? undefined : fabricRef.substring(sepIdx + 2);
    try {
      const swatches = await this.fabricService.getSwatchesByCode(
        mfrCode,
        group ? { group } : undefined
      );
      swatch = swatches[0];
    } catch (err) {
      if (err instanceof NotFoundError) {
        NotFoundError.popup(err.message, err.statusCode);
      } else {
        APIError.popup(err.message, err.statusCode);
      }
    }
    return swatch;
  }

  protected async getUmbrellaModel(model: string): Promise<UmbrellaModel> {
    let umbrella = {} as UmbrellaModel;
    try {
      const res: SingleProductRes = (await this.productService.getSingleProduct(
        model
      )) as SingleProductRes;
      umbrella = res.product as any;
    } catch (err) {
      if (err instanceof NotFoundError) {
        NotFoundError.popup(err.message, err.statusCode);
      } else {
        APIError.popup(err.message, err.statusCode);
      }
    }
    return umbrella;
  }

  protected async getBaseModel(model: string): Promise<BaseModel> {
    let base = {} as BaseModel;
    try {
      const res: SingleProductRes = (await this.productService.getSingleProduct(
        model
      )) as SingleProductRes;
      base = res.product as any;
    } catch (err) {
      if (err instanceof NotFoundError) {
        NotFoundError.popup(err.message, err.statusCode);
      } else {
        APIError.popup(err.message, err.statusCode);
      }
    }
    return base;
  }

  protected async getUmbrellaProductLines(handle: string): Promise<Collection> {
    let umbrellas: Collection[] = [];
    try {
      const res = await this.productService.getProducts({
        category: ProductType.Umbrella,
        sort: "productline",
      });
      umbrellas = res.filter((umbrella) => umbrella.handle === handle);
    } catch (err) {
      if (err instanceof NotFoundError) {
        NotFoundError.popup(err.message, err.statusCode);
      } else {
        APIError.popup(err.message, err.statusCode);
      }
    }
    return umbrellas[0];
  }

  protected getFinish(stringHandle: FinishHandle): Finish {
    const handle = stringHandle as FinishHandle;
    let obj = {} as Finish;
    switch (handle) {
      case FinishHandle.MS:
        obj = { handle: FinishHandle.MS, display_name: FinishDisplayName.MS };
        break;
      case FinishHandle.WG:
        obj = { handle: FinishHandle.WG, display_name: FinishDisplayName.WG };
        break;
      case FinishHandle.BK:
        obj = { handle: FinishHandle.BK, display_name: FinishDisplayName.BK };
        break;
      case FinishHandle.WH:
        obj = { handle: FinishHandle.WH, display_name: FinishDisplayName.WH };
        break;
      case FinishHandle.BZ:
        obj = { handle: FinishHandle.BZ, display_name: FinishDisplayName.BZ };
        break;
      case FinishHandle.SR:
        obj = { handle: FinishHandle.SR, display_name: FinishDisplayName.SR };
        break;
      case FinishHandle.HW:
        obj = { handle: FinishHandle.HW, display_name: FinishDisplayName.HW };
        break;
      case FinishHandle.MSA:
        obj = { handle: FinishHandle.MSA, display_name: FinishDisplayName.MSA };
        break;
      case FinishHandle.SM:
        obj = { handle: FinishHandle.SM, display_name: FinishDisplayName.SM };
        break;
      case FinishHandle.SS:
        obj = { handle: FinishHandle.SS, display_name: FinishDisplayName.SS };
        break;
      case FinishHandle.GS:
        obj = { handle: FinishHandle.GS, display_name: FinishDisplayName.GS };
        break;
      case FinishHandle.ASH:
        obj = { handle: FinishHandle.ASH, display_name: FinishDisplayName.ASH };
        break;
      case FinishHandle.GBK:
        obj = { handle: FinishHandle.GBK, display_name: FinishDisplayName.GBK };
        break;
      case FinishHandle.CB:
        obj = { handle: FinishHandle.CB, display_name: FinishDisplayName.CB };
        break;
      case FinishHandle.CH:
        obj = { handle: FinishHandle.CH, display_name: FinishDisplayName.CH };
        break;
    }
    return obj;
  }

  protected isEmptyObject(obj: any): boolean {
    return (
      Object.keys(obj).length === 0 &&
      Object.getPrototypeOf(obj) === Object.prototype
    );
  }
}
